"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Layers } from "lucide-react";
import { berekenStand, type Aftelstand } from "@/lib/format";

export type VolgendeWedstrijdData = {
  id: number;
  /** Plaats, zoals TRS de wedstrijd noemt. */
  naam: string;
  /** Volledige datum, al opgemaakt op de server. */
  datumTekst: string;
  klassen: string | null;
  /** Moment waarop het begint, als ISO-tekst. Daar telt de klok naartoe. */
  startISO: string;
  /** Op de server berekend, zodat de eerste render van de browser er niet
      van afwijkt en React niet gaat klagen over verschil. */
  beginStand: Aftelstand;
};

const twee = (n: number) => String(n).padStart(2, "0");

// De eerstvolgende wedstrijd, groot bovenaan de homepage.
//
// De plaats is het grootst: dat is waar iemand op zoekt. Daaronder telt de klok
// af naar de aanvang, zoals de F1-app dat doet — een getal dat elke keer dat je
// de app opent iets kleiner is, is een sterkere reden om terug te komen dan een
// knop met "Details" erop. De hele kaart is de link.
export function VolgendeWedstrijd({ wedstrijd }: { wedstrijd: VolgendeWedstrijdData }) {
  const [stand, setStand] = useState<Aftelstand>(wedstrijd.beginStand);

  useEffect(() => {
    // Elke twintig seconden: fijn genoeg voor een klok op minuten, en het houdt
    // de telefoon met rust.
    const tik = () => setStand(berekenStand(wedstrijd.startISO));
    tik();
    const timer = setInterval(tik, 20000);
    return () => clearInterval(timer);
  }, [wedstrijd.startISO]);

  return (
    <Link
      href={`/wedstrijd/${wedstrijd.id}`}
      className="block overflow-hidden rounded-xl border-2 border-primary bg-card transition-colors active:bg-[#24456b]"
    >
      <div className="px-4 pb-6 pt-7 text-center">
        <h3 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-white">
          {wedstrijd.naam}
        </h3>

        <p className="mt-3 text-lg font-semibold capitalize italic text-primary">
          {wedstrijd.datumTekst}
        </p>

        {wedstrijd.klassen && (
          <p className="mt-4 inline-flex items-center justify-center gap-1.5 text-sm text-soft-foreground">
            <Layers className="size-4 shrink-0 text-primary" aria-hidden />
            {wedstrijd.klassen}
          </p>
        )}
      </div>

      <div className="border-t-2 border-primary/40 bg-[#0f2233] px-4 py-4 text-center">
        {stand.verstreken ? (
          <p className="font-display text-lg font-black uppercase tracking-wide text-primary">
            Vandaag
          </p>
        ) : (
          <div className="flex items-start justify-center gap-2">
            <Deel waarde={twee(stand.dagen)} label={stand.dagen === 1 ? "dag" : "dagen"} />
            <Dubbelepunt />
            <Deel waarde={twee(stand.uren)} label="uur" />
            <Dubbelepunt />
            <Deel waarde={twee(stand.minuten)} label="min" />
          </div>
        )}
      </div>
    </Link>
  );
}

function Deel({ waarde, label }: { waarde: string; label: string }) {
  return (
    <span className="flex w-16 flex-col items-center">
      <span className="font-display text-4xl font-black leading-none tabular-nums text-white">
        {waarde}
      </span>
      <span className="mt-1 text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
    </span>
  );
}

function Dubbelepunt() {
  return (
    <span className="font-display text-4xl font-black leading-none text-primary/50" aria-hidden>
      :
    </span>
  );
}
